import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { LockKeyhole, FileText, CreditCard, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function TrialExpiredPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const onLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <div
            className="min-h-screen bg-navy relative overflow-hidden"
            data-testid="trial-expired-page"
        >
            <div className="bg-grid absolute inset-0 opacity-20"></div>
            <header className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                <Logo className="h-20 w-auto bg-white rounded-md p-1" />
                <Button
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                    onClick={onLogout}
                    data-testid="trial-expired-logout"
                >
                    <LogOut size={14} className="mr-2" /> Esci
                </Button>
            </header>

            <main className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 py-16 text-white">
                <div className="w-16 h-16 rounded-2xl bg-amber-brand text-navy flex items-center justify-center mb-8">
                    <LockKeyhole size={28} />
                </div>
                <div className="label-eyebrow text-amber-brand mb-3">
                    Periodo di prova terminato
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-tight mb-5">
                    Il tuo trial di 3 giorni
                    <br />
                    <span className="italic text-amber-brand">è finito.</span>
                </h1>
                <p className="text-white/80 text-lg leading-relaxed mb-12 max-w-xl">
                    Per continuare a usare Staff Flow 360 attiva un
                    abbonamento. Bastano due passi: completa i dati di
                    fatturazione, poi scegli il piano.
                </p>

                <div className="grid md:grid-cols-2 gap-5 mb-10">
                    <Step
                        n="1"
                        icon={FileText}
                        title="Dati di fatturazione"
                        text="Inserisci ragione sociale, P.IVA/CF, indirizzo, PEC/SDI e contatti."
                        done={user?.has_billing_info}
                    />
                    <Step
                        n="2"
                        icon={CreditCard}
                        title="Scegli e paga il piano"
                        text="Base €9/mese o Pro €19/mese. Pagamento sicuro via Stripe."
                        done={false}
                    />
                </div>

                <div className="flex flex-wrap gap-3">
                    {!user?.has_billing_info ? (
                        <Link
                            to="/billing/info"
                            state={{ next: "/pricing" }}
                        >
                            <Button
                                size="lg"
                                className="bg-amber-brand text-navy hover:bg-amber-brand/90 font-bold lift"
                                data-testid="trial-expired-billing-cta"
                            >
                                <FileText size={16} className="mr-2" />
                                Inserisci dati di fatturazione
                            </Button>
                        </Link>
                    ) : (
                        <Link to="/pricing">
                            <Button
                                size="lg"
                                className="bg-amber-brand text-navy hover:bg-amber-brand/90 font-bold lift"
                                data-testid="trial-expired-pricing-cta"
                            >
                                <CreditCard size={16} className="mr-2" />
                                Scegli un piano
                            </Button>
                        </Link>
                    )}
                </div>

                <div className="mt-16 text-xs text-white/40">
                    Staff Flow 360 è un prodotto di Makai · P.IVA 09364410969
                </div>
            </main>
        </div>
    );
}

function Step({ n, icon: Icon, title, text, done }) {
    return (
        <div
            className={`rounded-xl p-6 border ${done ? "bg-amber-brand/20 border-amber-brand" : "bg-white/5 border-white/15"}`}
        >
            <div className="flex items-center gap-3 mb-3">
                <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${done ? "bg-amber-brand text-navy" : "bg-white/10 text-white"}`}
                >
                    {done ? "✓" : n}
                </div>
                <Icon size={18} className={done ? "text-amber-brand" : "text-white/70"} />
            </div>
            <div className="font-display text-xl mb-1">{title}</div>
            <div className={`text-sm leading-relaxed ${done ? "text-white/70" : "text-white/60"}`}>
                {text}
            </div>
        </div>
    );
}
